import { useEffect } from 'react';

interface LocalBusinessSchemaProps {
  locationName: string;
  addressLocality: string;
  addressRegion: string;
  postalCode?: string;
  streetAddress?: string;
  description?: string;
  services?: string[];
}

const LocalBusinessSchema: React.FC<LocalBusinessSchemaProps> = ({
  locationName,
  addressLocality,
  addressRegion,
  postalCode,
  streetAddress,
  description,
  services = []
}) => {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "GeneralContractor",
      "name": `Blue Knight Contracting & Construction - ${locationName}`,
      "description": description || `Professional construction and contracting services in ${addressLocality}, ${addressRegion}`,
      "url": window.location.href,
      "telephone": "(416) 555-BLUE",
      "email": "info@blueknightcontracting.ca",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": addressLocality,
        "addressRegion": addressRegion,
        "addressCountry": "CA",
        ...(postalCode && { "postalCode": postalCode }),
        ...(streetAddress && { "streetAddress": streetAddress })
      },
      "areaServed": {
        "@type": "City",
        "name": addressLocality,
        "addressRegion": addressRegion,
        "addressCountry": "CA"
      },
      "serviceType": services.length > 0 ? services : [
        "General Contracting",
        "Residential Construction",
        "Commercial Construction",
        "Industrial Construction",
        "Skilled Trades",
        "Heavy Equipment Services",
        "Specialty Contracting",
        "Design Engineering"
      ],
      "hasCredential": [
        {
          "@type": "EducationalOccupationalCredential",
          "name": "Licensed General Contractor"
        },
        {
          "@type": "EducationalOccupationalCredential",
          "name": "Fully Insured"
        }
      ],
      "foundingDate": "2004",
      "numberOfEmployees": "50+",
      "priceRange": "$$-$$$",
      "paymentAccepted": ["Cash", "Check", "Credit Card"],
      "currenciesAccepted": "CAD",
      "openingHours": [
        "Mo-Fr 07:00-18:00",
        "Sa 08:00-16:00"
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "150",
        "bestRating": "5",
        "worstRating": "1"
      }
    };

    // Remove existing schema
    const existingSchema = document.querySelector('script[type="application/ld+json"][data-location-schema]');
    if (existingSchema) {
      existingSchema.remove();
    }

    // Add new schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-location-schema', 'true');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const schemaToRemove = document.querySelector('script[type="application/ld+json"][data-location-schema]');
      if (schemaToRemove) {
        schemaToRemove.remove();
      }
    };
  }, [locationName, addressLocality, addressRegion, postalCode, streetAddress, description, services]);

  return null;
};

export default LocalBusinessSchema;